import PropTypes from 'prop-types'
import React, { memo, useCallback, useEffect, useState } from 'react'
// Component
import PopupNotificationPage from '../../pages/PopupNotificationPage/PopupNotificationPage'
// Styled Component
import {
  ListWrapper,
  NotificationItem,
  ViewAll,
} from '../../layouts/DashboardLayout/DashboardLayoutStyled'
import { NotiWrapper } from '../MainSidebar/MainSidebarStyled'
// Ant design
import {
  BellFilled,
  CloseOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Dropdown,
  Menu,
  Tabs,
  Tooltip,
  message,
} from 'antd'
// Other
import { useKeycloak } from '@react-keycloak/web'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { blue } from '../../color'
import { prefix, useSSO, vapidKey } from '../../config'
import { EOFFICE_ADMIN, NOTIFICATION_STATUS } from '../../constants'
import utils from '../../utils'
import { subscribeToWebPush, unsubscribeFromWebPush } from '../../utils/webPush'
import PopupAppGrid from '../PopupAppGrid/PopupAppGrid'

const { TabPane } = Tabs

const MainHeaderBar = props => {
  const {
    authenticationStore,
    accountStore,
    history,
    companyStore,
    userStore,
    notificationStore,
  } = props

  const { unreadNotificationCount, unreadNewsCount } = notificationStore
  const {
    currentUserAvatar,
    currentUser,
    mustChangePassword,
  } = authenticationStore

  const [visibleNotification, setVisibleNotification] = useState(false)
  const [sendingTestNotification, setSendingTestNotification] = useState(false)
  const [changedTabsNotification, setChangedTabsNotification] = useState({
    status: false,
    onlyNewsNotification: false,
    isOpen: false,
  })

  const disabledKeyCloak = {
    keycloak: null,
  }
  const { keycloak, initialized } = useSSO ? useKeycloak() : disabledKeyCloak

  const clickLogout = useCallback(async () => {
    try {
      const endpoint = await unsubscribeFromWebPush()
      if (endpoint) {
        await notificationStore.deleteWebPushSubscription(endpoint)
      }
    } catch (error) {
      console.error(
        '[WebPush] Failed to remove the subscription on logout.',
        error
      )
    }

    accountStore.clearStore()
    companyStore.clearStore()
    userStore.clearStore()
    notificationStore.clearStore()
    authenticationStore.userLogout()

    if (keycloak) {
      keycloak.logout()
    }

    if (!useSSO) {
      history.replace('/')
    }
  }, [])

  useEffect(() => {
    let mounted = true
    subscribeToWebPush(vapidKey).then(subscription => {
      if (mounted && subscription) {
        notificationStore
          .saveWebPushSubscription(subscription)
          .catch(error =>
            console.error('[WebPush] Failed to save the subscription.', error)
          )
      }
    })
    return () => {
      mounted = false
    }
  }, [notificationStore])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    const onServiceWorkerMessage = async event => {
      if (event.data?.type !== 'PUSH_NOTIFICATION_RECEIVED') return
      const payload = event.data.payload || {}
      const data = payload.data || {}
      if (data.type === NOTIFICATION_STATUS.NEWS) {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: true,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNewsCount()
        }
      } else {
        if (changedTabsNotification.isOpen) {
          setChangedTabsNotification({
            status: true,
            onlyNewsNotification: false,
            isOpen: true,
          })
        } else {
          await notificationStore.getUnreadNotificationCount()
        }
      }
      notificationStore.setNotificationType(data.type)
      const body = payload.notification?.body || data.body || ''
      message.info(body ? `Thông báo mới: ${body}` : 'Bạn có thông báo mới')
    }
    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage)
    return () =>
      navigator.serviceWorker.removeEventListener(
        'message',
        onServiceWorkerMessage
      )
  }, [changedTabsNotification])

  const menu = (
    <Menu>
      <Menu.Item
        onClick={() => history.push('/my-profile')}
        style={{ color: blue }}>
        <UserOutlined style={{ color: blue, marginRight: '5px' }} />
        Thông Tin Cá Nhân
      </Menu.Item>
      <Menu.Item onClick={() => clickLogout()} danger>
        <LogoutOutlined style={{ marginRight: '5px' }} />
        Đăng Xuất
      </Menu.Item>
    </Menu>
  )

  const onChangeTabsNotification = tab => {
    setChangedTabsNotification({
      status: true,
      onlyNewsNotification: tab === 'news',
      isOpen: true,
    })
  }

  const sendTestNotification = async () => {
    setSendingTestNotification(true)
    try {
      const response = await notificationStore.sendTestWebPush()
      const sentCount = response.data.sent_count
      message.success(
        `Đã gửi thông báo kiểm thử đến ${sentCount} thiết bị Web Push.`
      )
    } catch (error) {
      message.error(
        error.response?.data?.message ||
          error.message ||
          error.vi ||
          'Không thể gửi thông báo kiểm thử.'
      )
    } finally {
      setSendingTestNotification(false)
    }
  }

  const canSendTestNotification =
    currentUser?.roles?.includes(EOFFICE_ADMIN) === true

  const notiWrapper = (
    <NotiWrapper>
      <Tabs
        onChange={onChangeTabsNotification}
        defaultActiveKey="1"
        tabBarExtraContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              loading={sendingTestNotification}
              onClick={sendTestNotification}>
              Test thông báo
            </Button>
            {canSendTestNotification && <></>}
            <Tooltip title={'Đóng'}>
              <span
                className={'close-notification'}
                onClick={() => setVisibleNotification(false)}>
                <CloseOutlined />
              </span>
            </Tooltip>
          </div>
        }>
        <TabPane
          tab={
            <Badge
              count={
                unreadNotificationCount > 99 ? '99+' : unreadNotificationCount
              }>
              <span style={{ paddingRight: 13 }}>Thông báo</span>
            </Badge>
          }
          key="notification">
          <ListWrapper>
            <ViewAll
              onClick={() => {
                setVisibleNotification(false)
                const queryStringParams = queryString.stringify({
                  only_news_notification: false,
                })
                history.push(`/notification?${queryStringParams}`)
              }}>
              <a>Xem tất cả</a>
            </ViewAll>
            <PopupNotificationPage
              changedTabsNotification={changedTabsNotification}
              onlyNewsNotification={false}
              visibleNotification={visibleNotification}
              setVisibleNotification={() => setVisibleNotification(false)}
            />
          </ListWrapper>
        </TabPane>
        <TabPane
          tab={
            <Badge count={unreadNewsCount > 99 ? '99+' : unreadNewsCount}>
              <span style={{ paddingRight: 13 }}>Thông báo chung</span>
            </Badge>
          }
          key="news">
          <ListWrapper>
            <ViewAll
              onClick={() => {
                setVisibleNotification(false)
                const queryStringParams = queryString.stringify({
                  only_news_notification: true,
                })
                history.push(`/notification?${queryStringParams}`)
              }}>
              <a>Xem tất cả</a>
            </ViewAll>
            <PopupNotificationPage
              changedTabsNotification={changedTabsNotification}
              onlyNewsNotification={true}
              setVisibleNotification={() => setVisibleNotification(false)}
            />
          </ListWrapper>
        </TabPane>
      </Tabs>
    </NotiWrapper>
  )

  const unreadTotalCount = unreadNewsCount + unreadNotificationCount

  return (
    <>
      <div className={'logo'}>
        <img
          onClick={() => history.push('/dashboard')}
          src={`${process.env.PUBLIC_URL}/assets/photos/portal_logo_white.png`}
          alt="logo"
          className="logoImg"
        />
      </div>
      {mustChangePassword && (
        <Tooltip placement="top" title={'Mật khẩu của bạn đã quá hạn'}>
          <Alert
            message={
              <span>
                Mật khẩu của bạn đã quá hạn. Vui lòng đổi mật khẩu{' '}
                <a
                  href={`${
                    process.env.REACT_APP_API_URL.includes('localhost')
                      ? 'http://localhost:3000'
                      : process.env.REACT_APP_API_URL
                  }/my-profile/change-password`}>
                  tại đây
                </a>
              </span>
            }
            type="warning"
            showIcon
            closable
            style={{ marginRight: 10 }}
          />
        </Tooltip>
      )}

      <Tooltip placement="top" title={'Hướng dẫn sử dụng'}>
        <a
          href={`${
            process.env.PUBLIC_URL
          }/guide/HDSD_v1.zip?date=${moment().format('YYYY-MM-DD-HH-mm-ss')}`}
          className="sidebarItem avatar"
          style={{
            fontWeight: 500,
            fontSize: 14,
            textDecoration: 'underline',
          }}>
          Hướng dẫn sử dụng
        </a>
      </Tooltip>

      <PopupAppGrid />

      <NotificationItem
        className="sidebarItem"
        showNotification={visibleNotification}>
        <Tooltip placement="bottom" title={'Thông báo'}>
          <Dropdown
            overlay={notiWrapper}
            placement="bottomRight"
            trigger={['click']}
            visible={visibleNotification}
            onVisibleChange={flag => setVisibleNotification(flag)}>
            <div
              className={'notificationItemBox'}
              onClick={() => {
                if (!visibleNotification) {
                  setChangedTabsNotification({
                    status: true,
                    onlyNewsNotification:
                      changedTabsNotification.onlyNewsNotification,
                    isOpen: true,
                  })
                }
                setVisibleNotification(!visibleNotification)
              }}>
              <div style={{ position: 'relative' }}>
                <BellFilled style={{ color: '#fff', fontSize: 18 }} />
                <span
                  className={
                    unreadTotalCount !== 0
                      ? 'numberNotification'
                      : 'noNotification'
                  }>
                  {unreadTotalCount > 99 ? '99+' : unreadTotalCount}
                </span>
              </div>
            </div>
          </Dropdown>
        </Tooltip>
      </NotificationItem>
      <div style={{ marginRight: 6 }}>
        <Dropdown
          overlay={menu}
          placement="topLeft"
          trigger={['click']}
          className="sidebarItem">
          <Tooltip placement="bottom" title={'Tài khoản'}>
            <div
              onClick={() => setVisibleNotification(false)}
              className="sidebarItem avatar">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  size={28}
                  src={
                    currentUserAvatar && URL.createObjectURL(currentUserAvatar)
                  }
                />
                <span
                  style={{
                    margin: '0 7px',
                    fontWeight: 500,
                  }}>
                  {prefix}{' '}
                  {currentUser &&
                    utils.getNameInCapitalize(currentUser.name_uppercase)}
                </span>
              </div>
            </div>
          </Tooltip>
        </Dropdown>
      </div>
    </>
  )
}

MainHeaderBar.propTypes = {
  title: PropTypes.string,
}

export default memo(
  withRouter(
    inject(
      'authenticationStore',
      'accountStore',
      'userStore',
      'companyStore',
      'notificationStore',
      'loadingAnimationStore',
      'commonStore',
      'taskManagementStore',
      'taskManagementCommentStore'
    )(observer(MainHeaderBar))
  )
)
